/**
 * rewards-banner.js — Enrichment custom script (not a Sleek theme file).
 * Drives the mega-menu's three-state loyalty card
 * (snippets/mega-menu-rewards-banner.liquid): shows the customer's points
 * balance when signed in, "Sign in" when signed out on a browser that's
 * logged in before, or the original "Join" pitch for a genuinely new
 * visitor. Loaded unconditionally, on every page, logged in or not (see
 * layout/theme.liquid) -- the mega menu itself is sitewide chrome.
 *
 * ALL THREE STATES ARE ALREADY RENDERED SERVER-SIDE, HIDDEN AS NEEDED --
 * this script only ever TOGGLES which one shows (via the `!hidden`
 * class -- see the Liquid file's own comment for why that specific class,
 * not `.hidden` or the native `hidden` attribute, is required here: the
 * banner's own `.mega-menu__rewards-banner-content` rule sets `display`
 * LATER in theme.css than `.hidden` does, same specificity, so anything
 * else would silently lose the cascade). It never rewrites markup, never
 * changes an href -- both real states' links/copy already exist in the
 * DOM from Liquid.
 *
 * MULTIPLE INSTANCES: mega-menu.liquid renders this banner once per
 * top-level nav item that has a mega menu (one snippet render per loop
 * iteration), so there are typically several `[data-rewards-banner]`
 * elements in the DOM at once -- one per menu, all describing the same
 * customer. Every function below operates on the full NodeList and keeps
 * them in sync from one shared state.
 *
 * "REMEMBERED THIS DEVICE" COOKIE: a customer who is logged in on ANY
 * page (not just an account page -- the mega menu is sitewide) has this
 * cookie set/refreshed. It carries no identity, no customer ID, nothing
 * personal -- it is a pure boolean signal ("has this browser ever seen a
 * logged-in customer"), used only to choose "Sign in" over "Join" copy in
 * a nav element. 365-day expiry, refreshed on every logged-in pageview --
 * long enough that an infrequent-but-real returning customer still gets
 * "Sign in" rather than being re-pitched the programme; a browser that
 * genuinely never returns just ages out naturally, which is fine since
 * nothing depends on this cookie for anything beyond copy choice.
 *
 * BALANCE STATE: 2026-09-17 -- this used to own its own sessionStorage
 * cache/fetch/TTL logic directly. It now reads and writes exclusively
 * through window.EnrichmentRewardsState (assets/rewards-state.js, loaded
 * just before this file -- see layout/theme.liquid), the single shared
 * balance state for the whole page. Three consequences of that:
 *   - Render instantly from whatever's already cached (own fetch or
 *     another consumer's), never flashing the skeleton on a pageview
 *     that already has a recent-ish answer.
 *   - Subscribed to rewardsBalanceChange, so a redemption or cancellation
 *     made on /pages/rewards or the cart-drawer modal updates every menu
 *     instance on THIS page immediately, without waiting for this
 *     script's own next poll or menu-open check.
 *   - Every mega-menu `<details>` still refreshes on open (native
 *     `toggle` event, fires on every open/close however `open` was set --
 *     see DropdownController in header.js, which never
 *     dispatches this manually), but the "is this still fresh enough to
 *     skip a fetch" decision now lives in EnrichmentRewardsState.refresh()
 *     itself, shared with the interval poll below rather than duplicated
 *     here.
 *
 * Root-relative path, not routes.root_url-prefixed -- App Proxy paths are
 * never locale-prefixed, same rule every other loyalty-calling script in
 * this theme already follows (see loyalty-rewards.contract.md's
 * Transport section).
 */
(function () {
  var REMEMBERED_COOKIE_NAME = 'enrichment_has_account';
  var REMEMBERED_COOKIE_MAX_AGE_DAYS = 365;

  function getCookie(name) {
    var match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : null;
  }

  function setCookie(name, value, maxAgeDays) {
    var maxAgeSeconds = maxAgeDays * 24 * 60 * 60;
    document.cookie =
      name + '=' + encodeURIComponent(value) + '; max-age=' + maxAgeSeconds + '; path=/; SameSite=Lax';
  }

  function show(el) {
    if (el) el.classList.remove('!hidden');
  }
  function hide(el) {
    if (el) el.classList.add('!hidden');
  }

  var banners = document.querySelectorAll('[data-rewards-banner]');
  if (!banners.length) return; // banner setting is off, or this template has no mega menu

  // 2026-09-23, own follow-up suggestion accepted: "whole-card hover
  // affordance" -- only "View rewards" read as clickable before, even
  // though every state's real content (balance, birthday nudge, "Sign
  // in", "Join") all lead to the exact same place. A click anywhere on
  // the card that ISN'T on an actual link/button (which keeps its own
  // native behaviour untouched -- this never intercepts those) now
  // follows whichever link is genuinely visible for the current state.
  // offsetParent !== null is a reliable, cheap "is this actually
  // rendered and visible" check (a link inside a `.!hidden` ancestor,
  // or one that's itself `.!hidden`, has a null offsetParent) -- doesn't
  // need to know each state's own class-toggling details. Deliberately
  // plain navigation (no preventDefault dance, no keyboard handling
  // added) -- the card is a `<div>`, not a native interactive element,
  // so this is a convenience for mouse users on top of the real link
  // that's always still directly reachable/tabbable on its own.
  banners.forEach(function (banner) {
    banner.addEventListener('click', function (event) {
      if (event.target.closest('a, button')) return;
      var links = banner.querySelectorAll('a[href]');
      for (var i = 0; i < links.length; i++) {
        if (links[i].offsetParent !== null) {
          window.location.href = links[i].href;
          return;
        }
      }
    });
  });

  var loggedIn = banners[0].hasAttribute('data-logged-in');

  if (loggedIn) {
    // Refresh the "remembered" cookie on every logged-in pageview -- see
    // file header. A customer who's currently signed in obviously
    // qualifies as "has an account on this device".
    setCookie(REMEMBERED_COOKIE_NAME, '1', REMEMBERED_COOKIE_MAX_AGE_DAYS);

    // 2026-09-23: progress-to-next-reward bar. Pure function of
    // (balance, thresholdPoints, valueFormatted) so it's easy to reason
    // about/test in isolation from the DOM -- see renderBalanceOn below
    // for how the result is applied.
    //   - thresholdPoints <= 0 (not yet loaded, or a territory somehow
    //     misconfigured to 0) -> not ready, caller hides the whole bar.
    //   - balance already clears at least one full redemption -> show a
    //     full/celebratory bar ("You've earned...") rather than 0-toward-
    //     the-next-multiple, which would read as having made no progress
    //     right after crossing the threshold.
    //   - otherwise -> partial fill toward the next threshold.
    // 2026-09-23: label split into {prefix, value, suffix} instead of one
    // flat string -- own follow-up suggestion, "highlight the actual
    // reward value" (the £/currency amount is the real incentive; the
    // rest of the line is quiet supporting text). `value` is null for
    // the two fallback wordings (no valueFormatted supplied at all), in
    // which case the caller just renders `prefix` as the whole label
    // plain, unstyled -- there's no amount to highlight in that case.
    function computeRewardsProgress(pointsBalance, thresholdPoints, valueFormatted) {
      if (!(thresholdPoints > 0)) return null;
      var balance = Number(pointsBalance) || 0;
      var intoCurrentCycle = balance % thresholdPoints;
      if (balance > 0 && intoCurrentCycle === 0) {
        return valueFormatted
          ? { percent: 100, prefix: "You've earned ", value: valueFormatted, suffix: ' to redeem on your next order' }
          : { percent: 100, prefix: "You've earned a reward!", value: null, suffix: '' };
      }
      var remaining = thresholdPoints - intoCurrentCycle;
      var remainingText = remaining.toLocaleString() + ' point' + (remaining === 1 ? '' : 's');
      return valueFormatted
        ? { percent: Math.round((intoCurrentCycle / thresholdPoints) * 100), prefix: remainingText + ' to your next ', value: valueFormatted, suffix: ' off' }
        : { percent: Math.round((intoCurrentCycle / thresholdPoints) * 100), prefix: remainingText + ' to your next reward', value: null, suffix: '' };
    }

    function renderBalanceOn(banner, pointsBalance, hasBirthday, redemptionThresholdPoints, redemptionValueFormatted) {
      var skeletonEl = banner.querySelector('[data-rewards-banner-balance-skeleton]');
      var textEl = banner.querySelector('[data-rewards-banner-balance-text]');
      var birthdayNudgeEl = banner.querySelector('[data-rewards-banner-birthday-nudge]');
      var progressWrapEl = banner.querySelector('[data-rewards-banner-progress]');
      var progressFillEl = banner.querySelector('[data-rewards-banner-progress-fill]');
      var progressLabelEl = banner.querySelector('[data-rewards-banner-progress-label]');
      if (!textEl) return;
      textEl.textContent = Number(pointsBalance).toLocaleString() + ' points';
      hide(skeletonEl);
      show(textEl);

      var progress = computeRewardsProgress(pointsBalance, redemptionThresholdPoints, redemptionValueFormatted);
      if (progress && progressWrapEl && progressFillEl && progressLabelEl) {
        progressFillEl.style.width = progress.percent + '%';
        // 2026-09-23: built from DOM nodes, not an HTML string -- own
        // follow-up, "highlight the actual reward value" (the £ amount
        // is the real incentive; the rest of the line stays plain). Pure
        // textContent/createElement, never innerHTML, so this stays safe
        // regardless of what the server-supplied valueFormatted string
        // contains -- no escaping to get right, nothing to inject.
        progressLabelEl.textContent = '';
        progressLabelEl.appendChild(document.createTextNode(progress.prefix));
        if (progress.value) {
          var valueEl = document.createElement('span');
          valueEl.className = 'mega-menu__rewards-banner-progress-value';
          valueEl.textContent = progress.value;
          progressLabelEl.appendChild(valueEl);
          progressLabelEl.appendChild(document.createTextNode(progress.suffix));
        }
        show(progressWrapEl);
      } else {
        hide(progressWrapEl);
      }
      // 2026-09-16: on-site birthday nudge -- see the Liquid file's own
      // comment on why this exists (closing a discoverability gap;
      // enrollment is silent, so nothing else ever surfaces the
      // birthday-capture form's existence).
      //
      // 2026-10-20: this used to SHOW only and never hide, so that a
      // stale cached `false` could not re-show a nudge someone had just
      // answered. The defensive half was right; the asymmetry was the
      // bug. Never hiding also meant a true `hasBirthday: true` could
      // never take the nudge away, so saving a birthday on
      // /pages/rewards left the mega menu advertising it until the next
      // full page load -- the reported symptom.
      //
      // Both booleans are now acted on, and the original worry is still
      // handled, because only an EXPLICIT boolean does anything:
      // undefined/null (a partial set() from redeem/cancel, which carry
      // a pointsBalance and say nothing about birthdays) leaves the
      // nudge exactly as it is.
      if (birthdayNudgeEl) {
        if (hasBirthday === false) show(birthdayNudgeEl);
        else if (hasBirthday === true) hide(birthdayNudgeEl);
      }
    }

    function renderBalanceEverywhere(state) {
      if (!state) return;
      if (state.locked) {
        hideBalanceEverywhere();
        return;
      }
      banners.forEach(function (banner) {
        renderBalanceOn(
          banner,
          state.pointsBalance,
          state.hasBirthday,
          state.redemptionThresholdPoints,
          state.redemptionValueFormatted
        );
      });
    }

    function hideBalanceEverywhere() {
      banners.forEach(function (banner) {
        hide(banner.querySelector('[data-rewards-banner-balance]'));
      });
    }

    var meUrl = banners[0].dataset.meUrl;

    // Render immediately from whatever's already known (this tab's own
    // earlier fetch, or another consumer's), then let refresh() below
    // decide whether that's fresh enough or worth a background update --
    // never a skeleton flash for state we already have an answer for.
    renderBalanceEverywhere(window.EnrichmentRewardsState.get());

    if (meUrl) {
      window.EnrichmentRewardsState.refresh(meUrl).then(renderBalanceEverywhere);
      window.EnrichmentRewardsState.startAutoRefresh(meUrl);
    }

    // Stay in sync with changes made anywhere else on the page (a
    // redemption on /pages/rewards, the cart-drawer modal, etc.) --
    // updates every menu instance the instant it happens, not on this
    // script's own next poll or menu-open check.
    window.EnrichmentRewardsState.subscribe(function (state) {
      renderBalanceEverywhere(state);
    });

    // Live refresh on mega-menu open. isFresh/refresh's own "don't
    // hammer" decision is EnrichmentRewardsState's job now, shared with
    // the interval poll above -- this just triggers the check.
    banners.forEach(function (banner) {
      var details = banner.closest('details');
      if (!details || !meUrl) return;
      details.addEventListener('toggle', function () {
        if (!details.open) return;
        window.EnrichmentRewardsState.refresh(meUrl).then(renderBalanceEverywhere);
      });
    });

    return;
  }

  // Signed out -- choose Join vs Sign in based on the remembered-device
  // cookie. Join is already the server-rendered default (see the Liquid
  // file's own comment on why), so there's nothing to do in the common
  // "never seen before" case; only swap when the cookie says otherwise.
  if (getCookie(REMEMBERED_COOKIE_NAME)) {
    banners.forEach(function (banner) {
      banner.querySelectorAll('[data-rewards-banner-join]').forEach(hide);
      banner.querySelectorAll('[data-rewards-banner-signin]').forEach(show);
    });
  }
})();
